import { forwardRef, useId } from 'react'

import classNames from 'classnames'

import {
  BasicButton,
  BasicButtonProps,
  BasicButtonVariant,
} from '@/Atomic/Atoms/BasicButton'
import { Icon, IconColor } from '@/Atomic/Atoms/Icon'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'

import './Button.scss'

interface ButtonProps extends BasicButtonProps {
  title?: string
  variant?: BasicButtonVariant | 'icon'
  icon?: string
  iconColor?: IconColor
  iconSize?: 'sm' | 'md' | 'lg'
  buttonClassName?: string
  iconClassName?: string
  reverse?: boolean
  tooltip?: string
  ariaLabel?: string
  children?: React.ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      title,
      variant = 'primary',
      icon,
      iconColor,
      iconSize = 'md',
      buttonClassName,
      iconClassName,
      reverse,
      tooltip,
      ariaLabel,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const isIcon = variant === 'icon'
    const generatedId = useId()
    const buttonId = buttonProps.id ?? `button-${generatedId}`
    const resolvedAriaLabel = isIcon ? (ariaLabel ?? tooltip) : ariaLabel
    let IconButton

    if (isIcon && icon) {
      IconButton = (
        <Icon
          icon={icon}
          color={iconColor}
          size={iconSize}
          className={classNames('button-icon', iconClassName)}
        />
      )
    }

    const Button = (
      <>
        {icon && (
          <Icon
            icon={icon}
            size={iconSize}
            className={classNames('icon-button', iconClassName)}
          />
        )}
        {children ?? title}
      </>
    )

    return (
      <>
        <BasicButton
          ref={ref}
          id={buttonId}
          basicVariant={isIcon ? 'ghost' : variant}
          className={classNames(
            { 'button-reverse': reverse, 'icon-button': variant === 'icon' },
            buttonClassName,
          )}
          aria-label={resolvedAriaLabel}
          {...buttonProps}
        >
          {isIcon ? IconButton : Button}
        </BasicButton>
        {tooltip && <Tooltip anchorSelect={`#${buttonId}`} content={tooltip} />}
      </>
    )
  },
)

Button.displayName = 'Button'
